import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastOptions from "../../../utils/toastOptions";
import { ModalAddEdit } from "../../../containers";
import { IOrderLot } from "../../../models/IOrderLot";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { currencyMask, dateMask, displayDate, handleFocus } from "../../../utils/masks";
import { getValidator } from "../../../utils/validatorsYup";
import moment from "../../../utils/momentConfig";

interface OrderLotAddModalProps {
  open: boolean;
  afterHandleSubmit: () => void;
  afterCloseModal: () => void;
  type: string;
}

export default function OrderLotAddModal(props: OrderLotAddModalProps) {
  const { open, afterHandleSubmit, afterCloseModal } = props;
  const [orderLot, setOrderLot] = useState<IOrderLot>();
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModelOpen] = useState<boolean>(false);
  const [startAt, setStartAt] = useState<Date>();
  const [endAt, setEndAt] = useState<Date>();
  const [summary, setSummary] = useState<any>({ totalItems: 0, totalAmountDue: 0, totalPaid: 0 });

  const schema = yup.object().shape({
    startAt: getValidator({ isDate: true }).required("Início obrigatório"),
    endAt: getValidator({ isDate: true }).required("Final obrigatório")
  });

  const { register, handleSubmit, reset, errors, formState, clearErrors } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    reset();
    clearErrors();
    setModelOpen(open);
    if (open) {
      // Reset form when modal opens
      const today = new Date();
      setOrderLot({endAt: today, type: props.type} as IOrderLot);
      setEndAt(today);
      setSummary({ totalItems: 0, totalAmountDue: 0, totalPaid: 0 });
      setStartAt(undefined);
    }
  }, [open]);

  const onSubmit = async (data: IOrderLot) => {
    try {
      setLoading(true);
      await api.post(`/order-lots/${props.type}`, data);
      toast.success("Lote criado com sucesso!", toastOptions);
      handleCloseModal();
      afterHandleSubmit();
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      afterCloseModal();
    }
  };

  const handleCloseModal = () => {
    setModelOpen(false);
    reset();
    afterCloseModal();
  };

  const handlePeriodChange = async () => {
    try {
      if (!startAt || !endAt) {
        return;
      }

      setLoading(true);

      let requestParams = {
        startAt: startAt,
        endAt: endAt
      } as any;

      const { data } = await api.get(`orders/${props.type}/stats`, { params: requestParams });

      setSummary({
        totalItems: data.totalItems,
        itemsBySize: data.itemsBySize,
        totalAmountDue: data.totalAmountDue,
        totalPaid: data.totalPaid
      });

    } catch (e: any) {
        e.messages?.forEach((m: string) => toast.warning(m, toastOptions));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (startAt && endAt) {
      handlePeriodChange();
    }
  }, [startAt, endAt]);

  if (!open) return null;

  return (<ModalAddEdit<IOrderLot>
    title="Novo Lote de Pedidos"
    isOpen={modalOpen}
    closeModal={handleClose}
    handleCloseModal={handleCloseModal}
    children={
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          return handleSubmit<IOrderLot>(onSubmit)(e);
        }}
      >
        <div className="uk-modal-body">
          <h5 className="uk-text-small uk-margin-remove-top uk-text-primary">
            <FontAwesomeIcon icon={faExclamationCircle} className="uk-margin-small-right" />
            Informe o período para criação do lote de pedidos <br/>
          </h5>
          <h6 className="uk-text-small uk-margin-remove-top uk-text-light">
            Garanta que após a criação do lote não tenha novos pedidos no período informado. <br/>
          </h6>
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-1-4@m">
              <label className="uk-form-label" htmlFor="startAt">Início</label>
              <input
                type="text"
                id="startAt"
                name="startAt"
                placeholder="99/99/9999"
                className="uk-input uk-form-small"
                defaultValue={displayDate(orderLot?.startAt?.toISOString())}
                style={{ textTransform: "uppercase" }}
                autoComplete={"off"}
                onFocus={handleFocus}
                ref={register}
                disabled={loading}
                onChange={(e) => {
                  e.target.value = dateMask(e.target.value);
                }}
                onKeyUp={(e: any) => {
                  const maskedValue = dateMask(e.target.value);
                  e.target.value = maskedValue;
                  if (maskedValue && moment(maskedValue, 'DD/MM/YYYY', true).isValid()) {
                    setStartAt(moment(maskedValue, 'DD/MM/YYYY').toDate());
                  }
                }}
              />
              <small className="uk-text-danger">{errors.startAt?.message}</small>
            </div>
            <div className="uk-width-1-4@m">
              <label className="uk-form-label" htmlFor="endAt">Final</label>
              <input
                type="text"
                id="endAt"
                name="endAt"
                placeholder="99/99/9999"
                className="uk-input uk-form-small"
                defaultValue={displayDate(orderLot?.endAt?.toISOString())}
                style={{ textTransform: "uppercase" }}
                autoComplete={"off"}
                onFocus={handleFocus}
                ref={register}
                disabled={loading}
                onChange={(e) => {
                  e.target.value = dateMask(e.target.value);
                }}
                onKeyUp={(e: any) => {
                  const maskedValue = dateMask(e.target.value);
                  e.target.value = maskedValue;
                  if (maskedValue && moment(maskedValue, 'DD/MM/YYYY', true).isValid()) {
                    setEndAt(moment(maskedValue, 'DD/MM/YYYY').toDate());
                  }
                }}
              />
              <small className="uk-text-danger">{errors.endAt?.message}</small>
            </div>
            {summary.totalItems > 0 &&
              <div className="uk-width-1-1@m">
                <span className="uk-text-small">
                  Total de Itens: 
                </span>
                <span className="uk-margin-small-left uk-text-small uk-text-bold">
                  {summary.totalItems}
                </span>
                <span className="uk-text-small uk-margin-large-left">
                  Valor Devido: 
                </span>
                <span className="uk-margin-small-left uk-text-small uk-text-bold">
                  {currencyMask(summary.totalAmountDue)}
                </span>
              </div>
            }
          </div>
        </div>
        <div className="uk-modal-footer">
          <div
            className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
            uk-grid="true"
          >
            <button
              className="uk-button uk-button-default uk-button-small uk-modal-close uk-margin-left"
              onClick={handleCloseModal}
              type="button"
            >
              Cancelar
            </button>
            <button
              disabled={formState.isSubmitting || loading}
              className="uk-button uk-button-primary uk-button-small uk-margin-left"
              type="submit"
            >
              {(formState.isSubmitting) && <div uk-spinner="true; ratio:0.5"></div>}
              Salvar
            </button>
          </div>
        </div>
      </form>
    }
  />);
}